import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CalendarClock, ChevronRight } from 'lucide-react';
import { useGetList } from 'ra-core';
import { Link } from 'react-router';

/**
 * PaymentReminderCard
 * 
 * High-priority alerts for invoices due within 2 days or overdue.
 */
export const PaymentReminderCard = () => {
    const { data: invoices, isPending } = useGetList('customer_invoices', {
        pagination: { page: 1, perPage: 100 },
        filter: { status: 'due_soon' }, // This will trigger the or-logic in dataProvider for overdue as well if we adjust it, 
        // but for now let's just fetch sent/approved and filter locally for maximum precision.
        sort: { field: 'due_date', order: 'ASC' }
    });

    const unpaid = (invoices ?? []).filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
    
    // Final filtering to ensure we only show the immediate priority
    const today = new Date();
    today.setHours(0,0,0,0);
    const twoDaysLater = new Date(today);
    twoDaysLater.setDate(today.getDate() + 2);

    const highPriority = unpaid.filter(inv => {
        if (!inv.due_date) return false;
        const d = new Date(inv.due_date);
        return d <= twoDaysLater; // Overdue or due in <= 2 days
    }).slice(0, 5); // Just top 5

    if (isPending) return null;
    if (highPriority.length === 0) return null;

    return (
        <Card className="border-none shadow-md bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-l-amber-500 overflow-hidden">
            <CardHeader className="pb-2 border-b border-amber-100/50">
                <CardTitle className="text-xs font-black uppercase tracking-widest text-amber-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Priority Payment Reminders
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 p-0">
                <div className="divide-y divide-amber-100">
                    {highPriority.map((inv) => {
                        const isOverdue = new Date(inv.due_date!) < today;
                        return (
                            <Link 
                                key={inv.id} 
                                to={`/customer_invoices/${inv.id}/show`}
                                className="flex items-center justify-between p-4 hover:bg-amber-100/30 transition-colors group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-full ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                                        <CalendarClock className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-foreground">{inv.reference}</p>
                                        <p className="text-[10px] text-muted-foreground font-medium">
                                            {inv.customer_name} • <span className={isOverdue ? "text-red-500 font-bold" : ""}>
                                                {isOverdue ? 'Overdue' : 'Due Soon'} ({new Date(inv.due_date!).toLocaleDateString('en-IN')})
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        <p className="text-sm font-black text-foreground leading-none">
                                            ₹{(inv.total_amount || 0).toLocaleString('en-IN')}
                                        </p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
                <Link 
                    to="/customer_invoices?filter=%7B%22status%22%3A%22due_soon%22%7D" 
                    className="block p-3 text-center text-[10px] font-bold uppercase tracking-widest text-amber-700 hover:bg-amber-100/50 transition-colors border-t border-amber-100"
                >
                    View All Priority Invoices
                </Link>
            </CardContent>
        </Card>
    );
};
